import { useEffect, useState } from 'react';
import useFiltersState from '../../../../state/useFiltersState';
import ModuleFilters, { TgetFiltersList } from '../../../../components/ModuleFilters/ModuleFilters';
import { ICountryDetail } from '../../../../types/countries';
import { getListOfCountriesByOfferedService } from '../../../../utils/getCountries';
import errorMessage from '../../../../utils/errorMessage';
import { DownloadOutlined } from '@ant-design/icons';
import { getKAMUserFilter } from '../../../../services/orders';
import { IKamClient } from '../../../../types/kamClient.types';
import { paymentMethodLabelsValue, typeFilterRawOptionsValueAndName } from '../../../../utils/variablesToTranslate';
import useUsersState from '../../../../state/useUsersState';
import useLsTrelloState from '../../../../state/useLsTrelloState';
import { IbackofficeUserData } from '../../../../types/configuration.types';
import './LogisticsServicesFilters.scss';

const LogisticsServicesFilters = () => {
  const { logisticsServicesFilters, setLogisticsServicesFilters } = useFiltersState();
  const { users } = useUsersState();
  const { logisticServices } = useLsTrelloState();

  const [openMonthFilter, setOpenMonthFilter] = useState<boolean>(false);
  const [openServiceFilter, setOpenServiceFilter] = useState<boolean>(false);
  const [openAnnotationsFilter, setOpenAnnotationsFilter] = useState<boolean>(false);
  const [openOriginCountryFilter, setOpenOriginCountryFilter] = useState<boolean>(false);
  const [openTypeFilter, setOpenTypeFilter] = useState<boolean>(false);
  const [openPaymentMethodFilter, setOpenPaymentMethodFilter] = useState<boolean>(false);
  const [openResponsibleFilter, setOpenResponsibleFilter] = useState<boolean>(false);

  const [countriesOpenToLogistics, setCountriesOpenToLogistics] = useState<ICountryDetail[]>();
  const [openDestinationCountryFilter, setOpenDestinationCountryFilter] = useState<boolean>(false);
  const [openKAMFilter, setOpenKAMFilter] = useState<boolean>(false);
  const [kamOptionsObject, setKamOptionsObject] = useState<IKamClient[]>([]);

  const servicesRawOption = ['nacional', 'internacional', 'manual'];

  const catalogueFilterMapping = {
    Mes: 'months',
    Servicio: 'service',
    Origen: 'origin',
    Destino: 'destination',
    annotations: 'annotations',
    type: 'type',
    KAM: 'kam',
    'Método pago': 'paymentType',
    Responsable: 'responsible'
  };

  const annotationsFilterPresantationToValueObject = {
    'sin cotización': 'quote',
    'sin factura': 'invoice',
    'sin DNS': 'dns',
    'sin pago': 'payment',
    'servicios adicionales procesados': 'finished additional services',
    'servicios adicionales pendientes': 'pending additional services',
    'Cotizaciones por aprobar': 'quoteApproved',
    'Cotizaciones aprobadas sin gestionar': 'qouteWithOutManage',
    'Pendiente por factura': 'needInvoice'
  };

  const annotationsFilterPresantationToValueMapping = new Map(
    Object.entries(annotationsFilterPresantationToValueObject)
  );

  const monthFilterRawOptions = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre'
  ];

  const kamFilterRawOptions = kamOptionsObject.map(item => item?.email || '');
  const kamNameToCodeMap: Map<string, string> = new Map(kamOptionsObject?.map(type => [type.email, String(type.id)]));

  const typeFilterRawOptions = typeFilterRawOptionsValueAndName.map(item => item.name);

  const typeNameToCodeMap: Map<string, string> = new Map(
    typeFilterRawOptionsValueAndName?.map(type => [type.name, type.value])
  );

  const countryNameToCodeMap: Map<string, string> = countriesOpenToLogistics
    ? new Map(countriesOpenToLogistics?.map(countryData => [countryData.name, countryData.code]))
    : new Map();

  const monthToNumberMap: Map<string, string> = monthFilterRawOptions
    ? new Map(monthFilterRawOptions?.map((month, i) => [month, i + 1]))
    : new Map();

  const logisticsCountriesRawOptions =
    countriesOpenToLogistics && countriesOpenToLogistics.length
      ? countriesOpenToLogistics.reduce<string[]>((accum, current) => {
          if (current?.name) accum.push(current.name);
          return accum;
        }, [])
      : undefined;

  const paymentMethodRawOptionName = paymentMethodLabelsValue.map(item => item.name);

  const paymentMethodNameValue: Map<string, string> = new Map(
    paymentMethodLabelsValue?.map(type => [type.name, type.value])
  );

  const responsibleUserIds = [
    ...(logisticServices?.pendings || []),
    ...(logisticServices?.confirmed || []),
    ...(logisticServices?.actives || []),
    ...(logisticServices?.delayed || []),
    ...(logisticServices?.delivered || []),
    ...(logisticServices?.cancelled || []),
    ...(logisticServices?.inProcess || [])
  ].flatMap(logisticService => logisticService?.responsibleIds || []);

  const noneResponsible = {
    email: 'Sin responsables asignados',
    id: 0,
    firstName: '',
    lastName: ''
  } as IbackofficeUserData;

  const responsibleUsers = users.filter(user => responsibleUserIds.includes(user.id));

  const responsibleUserRawOptions = [noneResponsible, ...responsibleUsers].map(user => user.email);

  const responsibleUserEmailToIdMap = new Map(
    [noneResponsible, ...responsibleUsers].map(user => [user.email, String(user.id)])
  );

  const responsibleUserEmailToNameMap = new Map(
    responsibleUsers.map(user => [user.email, `${user.firstName} ${user.lastName}`])
  );

  const getCatalogueFiltersList: TgetFiltersList = (
    formModuleFilters,
    handleClickFilterOption,
    getFilterActiveStatus
  ) => [
    {
      title: 'Mes',
      openFunction: setOpenMonthFilter,
      openStatus: openMonthFilter,
      rawSelectOptions: formModuleFilters('Mes', monthFilterRawOptions, monthToNumberMap),
      handleChangeSelectedOption: handleClickFilterOption('months', 'value'),
      isFilterActive: getFilterActiveStatus('months')
    },
    {
      title: 'Método pago',
      openFunction: setOpenPaymentMethodFilter,
      openStatus: openPaymentMethodFilter,
      rawSelectOptions: formModuleFilters('Método pago', paymentMethodRawOptionName, paymentMethodNameValue),
      handleChangeSelectedOption: handleClickFilterOption('paymentType', 'value'),
      isFilterActive: getFilterActiveStatus('paymentType')
    },
    {
      title: 'Servicio',
      openFunction: setOpenServiceFilter,
      openStatus: openServiceFilter,
      rawSelectOptions: formModuleFilters('Servicio', servicesRawOption),
      handleChangeSelectedOption: handleClickFilterOption('service', 'value'),
      isFilterActive: getFilterActiveStatus('service')
    },
    {
      title: 'Origen',
      openFunction: setOpenOriginCountryFilter,
      openStatus: openOriginCountryFilter,
      rawSelectOptions: formModuleFilters('Origen', logisticsCountriesRawOptions, countryNameToCodeMap),
      handleChangeSelectedOption: handleClickFilterOption('origin', 'value'),
      isFilterActive: getFilterActiveStatus('origin'),
      showSearch: true,
      searchPlaceholder: 'Buscar origen',
      sortAlphabetically: true
    },
    {
      title: 'Destino',
      openFunction: setOpenDestinationCountryFilter,
      openStatus: openDestinationCountryFilter,
      rawSelectOptions: formModuleFilters('Destino', logisticsCountriesRawOptions, countryNameToCodeMap),
      handleChangeSelectedOption: handleClickFilterOption('destination', 'value'),
      isFilterActive: getFilterActiveStatus('destination'),
      showSearch: true,
      searchPlaceholder: 'Buscar destino',
      sortAlphabetically: true
    },
    {
      title: 'Anotaciones',
      openFunction: setOpenAnnotationsFilter,
      openStatus: openAnnotationsFilter,
      rawSelectOptions: formModuleFilters(
        'annotations',
        Object.keys(annotationsFilterPresantationToValueObject),
        annotationsFilterPresantationToValueMapping
      ),
      handleChangeSelectedOption: handleClickFilterOption('annotations', 'value'),
      isFilterActive: getFilterActiveStatus('annotations')
    },
    {
      title: 'Tipo',
      openFunction: setOpenTypeFilter,
      openStatus: openTypeFilter,
      rawSelectOptions: formModuleFilters('type', typeFilterRawOptions, typeNameToCodeMap),
      handleChangeSelectedOption: handleClickFilterOption('type', 'value'),
      isFilterActive: getFilterActiveStatus('type')
    },
    {
      title: 'KAM',
      openFunction: setOpenKAMFilter,
      openStatus: openKAMFilter,
      rawSelectOptions: formModuleFilters('KAM', kamFilterRawOptions, kamNameToCodeMap),
      handleChangeSelectedOption: handleClickFilterOption('kam', 'value'),
      isFilterActive: getFilterActiveStatus('kam')
    },
    {
      title: 'Responsable',
      openFunction: setOpenResponsibleFilter,
      openStatus: openResponsibleFilter,
      rawSelectOptions: formModuleFilters('Responsable', responsibleUserRawOptions, responsibleUserEmailToIdMap).map(
        option => ({
          ...option,
          lineTitle: responsibleUserEmailToNameMap.get(option.name)
        })
      ),
      handleChangeSelectedOption: handleClickFilterOption('responsible', 'value'),
      isFilterActive: getFilterActiveStatus('responsible')
    }
  ];

  const openMetabase = () => {
    window.open(
      'https://metabase.nudos.co/question/478-herramientas-en-bodega?country=&serial=&cliente=&Localidad=&modelo=',
      '_blank'
    );
  };

  const fetchKamFilters = async () => {
    try {
      const kamFilters = await getKAMUserFilter();
      if (kamFilters) {
        setKamOptionsObject(kamFilters);
      }
    } catch (error) {
      errorMessage();
    }
  };

  useEffect(() => {
    getListOfCountriesByOfferedService(setCountriesOpenToLogistics, 'logisticService');
    fetchKamFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="logisticsServicesFilters">
      <ModuleFilters
        moduleFilters={logisticsServicesFilters}
        setModuleFilters={setLogisticsServicesFilters}
        getFiltersList={getCatalogueFiltersList}
        moduleFiltersTitlesMapping={catalogueFilterMapping}
        customGap={32}
        moduleFiltersIconContainerCustomClass="moduleFiltersIconContainerCustomClass"
        optionTextCustomClass="optionTextCustomClassBussines"
        countryCondition
      />
      <div className="inventoryButton">
        <button className=" inventoryButton" onClick={openMetabase}>
          <div className="containerIcon">
            <DownloadOutlined />
          </div>
          Descargar Inventario
        </button>
      </div>
    </div>
  );
};

export default LogisticsServicesFilters;
